import { useCallback, useEffect, useState } from "react";
import { Bug, Lightbulb, MessageSquareText, ChevronDown, ChevronUp, Map, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { confirmDialog } from "../ConfirmHost";
import { Switch } from "../ui/switch";
import { FEEDBACK_STATUS_META, FeedbackComments } from "../FeedbackWidget";

const STATUSES = ["nieuw", "in_behandeling", "hertesten", "opgelost", "afgewezen"];

const AdminFeedbackRow = ({ item, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const st = FEEDBACK_STATUS_META[item.status] || FEEDBACK_STATUS_META.nieuw;
  const setStatus = async (status) => {
    try {
      await api.patch(`/admin/feedback/${item.id}`, { status });
      toast.success(`Status → ${FEEDBACK_STATUS_META[status].label}`);
      onRefresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const promote = async () => {
    try {
      await api.post(`/admin/feedback/${item.id}/promote`);
      toast.success("Melding staat nu op de publieke roadmap");
      onRefresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const removeItem = async () => {
    if (!(await confirmDialog(`Melding "${item.title}" definitief verwijderen?`, { title: "Melding verwijderen", confirmLabel: "Verwijderen", destructive: true }))) return;
    try {
      await api.delete(`/admin/feedback/${item.id}`);
      toast.success("Melding verwijderd");
      onRefresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  return (
    <div data-testid={`admin-feedback-row-${item.id}`} className="bg-card rounded-2xl p-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <button className="flex items-center gap-2 min-w-0 flex-1 text-left" onClick={() => setOpen((o) => !o)} data-testid={`admin-feedback-toggle-${item.id}`}>
          {item.type === "bug" ? <Bug className="w-4 h-4 text-red-500 shrink-0" /> : <Lightbulb className="w-4 h-4 text-amber-500 shrink-0" />}
          <div className="min-w-0">
            <p className="font-semibold text-sm truncate">{item.title}</p>
            <p className="text-xs text-muted-foreground truncate">{item.user_name} · {item.user_email} · {new Date(item.created_at).toLocaleDateString("nl-BE")}</p>
          </div>
          {open ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
        </button>
        <select data-testid={`admin-feedback-status-select-${item.id}`} value={item.status} onChange={(e) => setStatus(e.target.value)}
          className={`rounded-full text-xs font-bold px-3 py-1.5 border-0 outline-none cursor-pointer ${st.cls}`}>
          {STATUSES.map((s) => <option key={s} value={s}>{FEEDBACK_STATUS_META[s].label}</option>)}
        </select>
      </div>
      {open && (
        <div className="mt-3">
          <p className="text-sm text-muted-foreground leading-relaxed bg-secondary/50 rounded-xl p-3">{item.description}</p>
          <button data-testid={`admin-feedback-promote-${item.id}`} onClick={promote} disabled={!!item.roadmap_update_id}
            className={`mt-2 rounded-full px-4 py-1.5 text-xs font-bold inline-flex items-center gap-1.5 transition-[background-color] duration-200 ${item.roadmap_update_id ? "bg-secondary text-muted-foreground cursor-default" : "bg-primary text-primary-foreground hover:bg-[#1B4332]"}`}>
            <Map className="w-3.5 h-3.5" /> {item.roadmap_update_id ? "Staat op de roadmap" : "Zet op roadmap"}
          </button>
          <button data-testid={`admin-feedback-delete-${item.id}`} onClick={removeItem}
            className="mt-2 ml-2 rounded-full px-4 py-1.5 text-xs font-bold inline-flex items-center gap-1.5 bg-red-50 text-red-600 hover:bg-red-100 transition-[background-color] duration-200">
            <Trash2 className="w-3.5 h-3.5" /> Verwijderen
          </button>
          <FeedbackComments item={item} onCommentAdded={onRefresh} />
        </div>
      )}
    </div>
  );
};

export const FeedbackAdmin = () => {
  const [items, setItems] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [filter, setFilter] = useState("alle");

  const load = useCallback(() => {
    api.get("/admin/feedback").then((r) => setItems(r.data)).catch(() => {});
    api.get("/config/public").then((r) => setEnabled(!!r.data.feedback_enabled)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (val) => {
    setEnabled(val);
    try {
      await api.patch("/admin/settings", { feedback_enabled: val });
      toast.success(val ? "Feedbackknop staat AAN" : "Feedbackknop staat UIT");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setEnabled(!val);
    }
  };

  const shown = filter === "alle" ? items : items.filter((i) => i.status === filter);

  return (
    <div data-testid="admin-feedback-section" className="mb-10">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-heading font-bold text-lg flex items-center gap-2"><MessageSquareText className="w-5 h-5 text-[#B5838D]" /> Feedback & bugs ({items.length})</h2>
        <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
          Feedbackknop actief
          <Switch data-testid="admin-feedback-toggle-switch" checked={enabled} onCheckedChange={toggle} />
        </label>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {["alle", ...STATUSES].map((s) => (
          <button key={s} data-testid={`admin-feedback-filter-${s}`} onClick={() => setFilter(s)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-[background-color] duration-200 ${filter === s ? "bg-primary text-primary-foreground" : "bg-secondary hover:bg-border"}`}>
            {s === "alle" ? "Alle" : FEEDBACK_STATUS_META[s].label}
          </button>
        ))}
      </div>
      <div className="space-y-2" data-testid="admin-feedback-list">
        {shown.length === 0 && <p className="text-sm text-muted-foreground bg-card rounded-2xl p-5 text-center">Geen meldingen{filter !== "alle" ? " met deze status" : ""}.</p>}
        {shown.map((it) => <AdminFeedbackRow key={it.id} item={it} onRefresh={load} />)}
      </div>
    </div>
  );
};
